import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Select from 'react-select';
import { RefinementListProvided } from 'react-instantsearch-core';
import { isEqual } from 'lodash';
import { ConnectedRefinementOption } from '../../admin-dashboard-tables/interfaces';
import CheckBoxOption from './react-select-custom-child-components/checkbox-option';
import CustomMenuList from './react-select-custom-child-components/search-menu-list';
import NewValueContainer from './react-select-custom-child-components/new-value-container';

interface Props extends RefinementListProvided {
  attribute: string,
  onRefineCallback?: () => void,
}

function getInstantSelectDropdown(
  hasBorder: boolean,
  placeholder: string = '',
  extraOptions: ConnectedRefinementOption[] = [],
  label = '',
  sortOptions = false,
  isFullWidth = false,
) {
  const InstantSelectDropdown: React.FC<Props> = ({
    attribute,
    items,
    refine,
    onRefineCallback,
  }) => {
    const options: ConnectedRefinementOption[] = [
      ...extraOptions,
      ...items.map((item) => ({
        value: item.value,
        label: item.label,
      })),
    ];

    const sortedOptions = useMemo(() => {
      if (sortOptions) {
        return options.sort(
          (a, b) => {
            if (a.label < b.label) {
              return -1;
            }
            if (a.label > b.label) {
              return 1;
            }
            return 0;
          },
        );
      }
      return options;
    }, [sortOptions, options]);

    // Set component length based on the placeholder
    const length = `${(9 * placeholder.length) + 60}px`;

    // Custom sub-component styles
    const selectStyles = {
      control: (styles) => {
        return {
          ...styles,
          width: isFullWidth ? '100%' : length,
        };
      },
      option: (styles) => {
        return {
          ...styles,
          backgroundColor: 'white',
          color: 'inherit',
        };
      },
    };

    const containerRef = useRef<HTMLDivElement>(null);
    // Is the select box focused (and open?)
    const [isFocused, setIsFocused] = useState(false);
    // Internal state of the dropdown component
    const [selectedValue, setSelectedValue] = useState(() => {
      return items.filter((item) => item.isRefined)
        .map((item) => ({
          value: item.value,
          label: item.label,
        }));
    });
    // Contents of the select input search
    const [inputValue, setInputValue] = useState('');

    /**
     * Open or close the menu any time a click event is registered. Based
     * on the following conditions:
     *
     * Close the component and reset to empty search if
     * 1) The click is outside the component
     * 2) The menu (options dropdown) is not open
     * 3) The click is outside the menu
     *
     * Otherwise, keep it open
     */
    const onDomClick = (e) => {
      if (!containerRef || !containerRef.current) {
        return;
      }

      const menu = containerRef.current.querySelector('.algolia-select__menu');

      if (
        !containerRef.current.contains(e.target)
        || !menu
        || !menu.contains(e.target)
      ) {
        setIsFocused(false);
        setInputValue('');
      } else {
        setIsFocused(true);
      }
    };

    useEffect(() => {
      document.addEventListener('mousedown', onDomClick);

      return () => {
        document.removeEventListener('mousedown', onDomClick);
      };
    }, []);

    useEffect(() => {
      const newSelectedValue = items.filter((item) => item.isRefined)
        .map((item) => ({
          value: item.value,
          label: item.label,
        }));
      setSelectedValue(newSelectedValue);
    }, [items]);

    const onChangeHandler = useCallback((event: ConnectedRefinementOption | null) => {
      if (!event) {
        // TODO: Do we want to rest here if no event?
        return;
      }
      if (isEqual(event.value, ['all'])) {
        refine([]);
        if (onRefineCallback) {
          onRefineCallback();
        }
        return;
      }
      refine(event.value);
      if (onRefineCallback) {
        onRefineCallback();
      }
    }, [onRefineCallback]);

    /**
     * If a select-dropdown menu is opened, but it is not this menu,
     * close this menu.
     *
     * There is currently an issue with events bubbling when <SelectDropdown> component
     * controls are clicked, and so a custom event is emitted instead.
     *
     * This is a temporary work-around, and a long-term task exists that will involve
     * re-writing the <SelectDropdown> component:
     * cf. https://schweb.atlassian.net/browse/LPBUGS-1453
     *
     * @param event
     */
    const onSelectMenuOpen = (event) => {
      if (event?.detail?.emitter !== attribute) {
        setIsFocused(false);
      }
    };

    useEffect(() => {
      document.addEventListener('select-dropdown-opened', onSelectMenuOpen);

      return () => {
        document.removeEventListener('select-dropdown-opened', onSelectMenuOpen);
      };
    }, []);

    return (
      <div ref={containerRef}>
        <Select
          aria-label={label || placeholder}
          className={`algolia-select${hasBorder ? '' : ' no-border'}`}
          classNamePrefix="algolia-select"
          value={selectedValue}
          components={{
            MenuList: CustomMenuList,
            ValueContainer: NewValueContainer,
            Option: CheckBoxOption,
          }}
          onChange={(event) => {
            setIsFocused(false);
            onChangeHandler(event);
          }}
          isSearchable={false} // Turns default search off. Use custom search in MenuList component
          options={sortedOptions}
          placeholder={placeholder}
          styles={selectStyles}
          onInputChange={(val) => setInputValue(val)}
          menuIsOpen={isFocused || undefined}
          isFocused={isFocused || undefined}
          onMenuClose={() => setIsFocused(false)}
          onMenuOpen={() => {
            setIsFocused(true);
            containerRef.current?.dispatchEvent(new CustomEvent('select-dropdown-opened', {
              bubbles: true,
              detail: {
                emitter: attribute,
              },
            }));
          }}
          inputValue={inputValue}
          onMenuInputFocus={() => setIsFocused(true)}
          setInputValue={setInputValue}
          setIsFocused={setIsFocused}
        />
      </div>
    );
  };
  return InstantSelectDropdown;
}

export default getInstantSelectDropdown;
